import { createBaseTypeUserState, createSumerUserState } from '../utils'
import { parseRawAmount } from '../../../utils/parsing'
import { isFailedCall } from '../../../utils/multicall'
import { getCompoundV2Tokens } from '../../public-data/addresses/compoundV2'
import { decodeLensRows } from '../position-lens'
import {
  COMPOUND_V2_LENS_HEADER_BYTES,
  COMPOUND_V2_LENS_WORDS,
  usesCompoundV2Lens,
} from './userCallBuild'
import {
  LenderYieldComplete,
  LenderCrossPoolMeta,
  UserData,
} from '../utils/types'
import { getDisplayPrice, getOraclePrice } from '../utils/oraclePrice'
import { createMarketUid } from '../../../utils/marketUid'
import { isSumerType } from '@1delta/lender-registry'

export const getCompoundV2UserDataConverter = (
  lender: string,
  chainId: string,
  account: string,
  metaMap?: LenderCrossPoolMeta,
): [(data: any[]) => UserData | undefined, number] => {
  switch (lender) {
    default: {
      // Use same token ordering as build function
      const tokens = getCompoundV2Tokens(chainId, lender).sort((a, b) =>
        a.cToken.toLowerCase().localeCompare(b.cToken.toLowerCase()),
      )
      const viaLens = usesCompoundV2Lens(chainId)
      const expectedNumberOfCalls = viaLens ? 1 : tokens.length * 3 + 1
      return [
        (data: any[]) => {
          if (data.length !== expectedNumberOfCalls) {
            return undefined
          }

          const lendingPositions: { [asset: string]: any } = {}
          let totalDebt24h = 0
          let totalDeposits24h = 0

          // `getAssetsIn` is a SET-LEVEL read: it decides which of the markets
          // below actually back the account's debt. Without it every deposit
          // would score as non-collateral, turning a healthy account into one
          // with debt and no backing — so decline the set rather than guess.
          // The lens carries the same fact as `assetsInOk` + a per-row flag, and
          // an unanswered `getAssetsIn` must decline there too.
          let states: Map<number, CompoundV2MarketState>
          if (viaLens) {
            const blobs = data[0]
            if (
              isFailedCall(blobs) ||
              !Array.isArray(blobs) ||
              blobs.length !== 1
            ) {
              return undefined
            }
            const { header, rows } = decodeLensRows(
              blobs[0],
              COMPOUND_V2_LENS_WORDS,
              COMPOUND_V2_LENS_HEADER_BYTES,
            )
            // BITMASK, not equality. This byte is only `assetsInOk` today, so
            // `!== 1` works — but the Aave family's header was exactly this shape
            // until v4 turned it into a bitfield (`0x01` -> `0x03`), at which
            // point an equality check silently declined EVERY set on deploy day
            // while looking like a clean rollout. Reading bit 0 costs nothing and
            // survives the byte gaining a neighbour.
            if ((header[0] & 1) === 0) return undefined
            states = new Map(
              rows.map((r) => [
                r.index,
                {
                  deposits: r.words[0],
                  borrows: r.words[1],
                  shares: r.words[2],
                  // bit0 — the market is in `getAssetsIn`. bit1 says the values
                  // are one accrual behind because the market's own
                  // `accrueInterest` reverts (a broken IRM, e.g. 4 of Gamma's 6
                  // BNB markets). Reported rather than dropped: the per-token
                  // walk drops those markets entirely.
                  collateralEnabled: (r.flags & 1) !== 0,
                },
              ]),
            )
          } else {
            const assetsInRaw = data[data.length - 1]
            if (isFailedCall(assetsInRaw) || !Array.isArray(assetsInRaw)) {
              return undefined
            }
            const asstsIn = assetsInRaw.map((a: any) => a.toLowerCase())
            states = statesFromSlice(data, tokens, asstsIn)
          }

          for (let i = 0; i < tokens.length; i++) {
            const { cToken } = tokens[i]
            const key = createMarketUid(chainId, lender, cToken)
            const { dataForAsset, addedDebt, addedDeposits } =
              createCompoundV2Entry(
                states.get(i),
                key,
                cToken,
                metaMap![key],
                0,
              )
            if (!dataForAsset) continue
            totalDebt24h += addedDebt
            totalDeposits24h += addedDeposits
            lendingPositions[key] = dataForAsset
          }

          const payload = {
            chainId,
            account,
            lendingPositions,
            rewards: [],
          }
          const createState = isSumerType(lender)
            ? createSumerUserState
            : createBaseTypeUserState
          const userData = createState(
            payload,
            metaMap!,
            totalDeposits24h,
            totalDebt24h,
          )
          return userData
        },
        expectedNumberOfCalls,
      ]
    }
  }
}

/**
 * One market's user state, however it was read.
 *
 * The two read paths — the 3-call-per-cToken walk and the single `PositionLens`
 * call — differ only in how these four values are obtained. Everything below is
 * shared, so the paths cannot drift into reporting different positions.
 */
interface CompoundV2MarketState {
  deposits: bigint
  borrows: bigint
  shares: bigint
  collateralEnabled: boolean
}

/** Per-token walk: 3 consecutive slots per cToken, in build order. */
function statesFromSlice(
  data: any[],
  tokens: { cToken: string }[],
  assetsIn: string[],
): Map<number, CompoundV2MarketState> {
  const out = new Map<number, CompoundV2MarketState>()
  for (let i = 0; i < tokens.length; i++) {
    // A failed read (revert on some forks, or a dropped multicall chunk) is not
    // a zero balance — skip the market rather than reporting an empty position.
    if (isFailedCall(data[3 * i]) || isFailedCall(data[3 * i + 1])) continue
    out.set(i, {
      deposits: BigInt(data[3 * i].toString()),
      borrows: BigInt(data[3 * i + 1].toString()),
      shares: BigInt(data[3 * i + 2]?.toString() ?? '0'),
      collateralEnabled: Boolean(
        assetsIn?.includes(tokens[i].cToken.toLowerCase()),
      ),
    })
  }
  return out
}

function createCompoundV2Entry(
  st: CompoundV2MarketState | undefined,
  key: string,
  vToken: string,
  meta: LenderYieldComplete,
  claimableRewards: any,
) {
  // Absent means the read failed (per-token walk) or the lens probed the market
  // and found nothing — either way there is no row.
  if (!st) {
    return {
      dataForAsset: undefined,
      addedDeposits: 0,
      addedDebt: 0,
    }
  }

  const depositsRaw = st.deposits.toString()
  const borrowsRaw = st.borrows.toString()
  const depositShares = st.shares.toString()
  const collateralEnabled = st.collateralEnabled

  // skip zeros AND disabled collaterals
  if (!collateralEnabled && depositsRaw === '0' && borrowsRaw === '0') {
    return {
      dataForAsset: undefined,
      addedDeposits: 0,
      addedDebt: 0,
    }
  }

  const assetMeta = meta.asset
  const decimals = assetMeta?.decimals ?? 18
  const currentATokenBalance = parseRawAmount(depositsRaw, decimals)
  const currentVariableDebt = parseRawAmount(borrowsRaw, decimals)

  const price = getDisplayPrice(meta)
  const oPrice = getOraclePrice(meta)
  const priceHist = meta?.price?.priceUsd24h ?? price

  const dataForAsset = {
    marketUid: key,
    underlying: assetMeta.address,
    deposits: currentATokenBalance,
    debtStable: '0',
    debt: currentVariableDebt,
    depositsUSD: Number(currentATokenBalance) * price,
    debtStableUSD: 0,
    debtUSD: Number(currentVariableDebt) * price,
    depositsUSDOracle: Number(currentATokenBalance) * oPrice,
    debtStableUSDOracle: 0,
    debtUSDOracle: Number(currentVariableDebt) * oPrice,
    collateralEnabled,
    claimableRewards,
    depositShares,
  }

  return {
    dataForAsset,
    addedDeposits: Number(currentATokenBalance) * priceHist,
    addedDebt: Number(currentVariableDebt) * priceHist,
  }
}
